// Vaanmathi
import React, { useState, useEffect, useMemo, useContext } from "react";
import { Search, Users, Calendar, Clock, Loader, Edit3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "../../Context/ToastContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Button, Pagination, Row } from "react-bootstrap";
import * as XLSX from "xlsx";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const Employees = () => {
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payDate, setPayDate] = useState("");
  const [periodType, setPeriodType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      try {
        const userMobile =
          localStorage.getItem("userMobile") ||
          sessionStorage.getItem("userMobile");
        if (!userMobile) return;

        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        }

        const data = await response.json();
        // Handle both array and single object responses
        const adminList = Array.isArray(data) ? data : [data];

        // Find current user in admin list
        const currentUser = adminList.find(
          (admin) => admin.mobileNumber === userMobile
        );
        if (currentUser) {
          setCurrentUserRole(currentUser.role);
        }
      } catch (error) {
        console.error("Error fetching admin role:", error);
      }
    };

    fetchCurrentUserRole();
  }, []);

  // Check if current user is superadmin
  const isSuperAdmin = currentUserRole === "superadmin";

  // Custom styles for table without vertical borders and alternating colors
  const tableStyles = `
    .custom-table th,
    .custom-table td {
      border-left: none !important;
      border-right: none !important;
      border-top: 1px solid #dee2e6;
      border-bottom: 1px solid #dee2e6;
    }
    
    .custom-table thead th {
      background-color: #f8f9fa;
      border-bottom: 2px solid #dee2e6;
      border-top: none;
    }
    
    .custom-table tbody tr:nth-child(odd) {
      background-color:rgb(158, 158, 158);
    }
    
    .custom-table tbody tr:nth-child(even) {
      background-color: #ffffff;
    }
    
    .custom-table tbody tr:hover {
      background-color: #e9ecef !important;
    }
  `;

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");

      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, startDateFilter, endDateFilter, filterStatus]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots.filter(
      (item, index, arr) => arr.indexOf(item) === index
    );
  };

  // Fetch employee data from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );

        if (!response.ok) {
          throw new Error("Failed to fetch employee data");
        }

        const data = await response.json();

        // Handle both single object and array responses
        const employeeData = Array.isArray(data) ? data : [data];
        const filteredEmployees = employeeData.filter(
          (d) => d.companyKey === companyKey
        );
        setEmployees(filteredEmployees.reverse());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    if (searchTerm) {
      filtered = filtered.filter((employee) => {
        const searchLower = searchTerm.toLowerCase();

        const salaryData = employee.salary ? JSON.parse(employee.salary) : [];
        const overtimeSalaryData = employee.overTimeSalary
          ? JSON.parse(employee.overTimeSalary)
          : [];

        const salaryAmount = salaryData.length > 0 ? salaryData[0].amount : "";
        const overtimeSalaryAmount =
          overtimeSalaryData.length > 0 ? overtimeSalaryData[0].amount : "";

        const joiningDateFormatted = employee.approval_date
          ? employee.approval_date.split("-").join("/")
          : "";

        const joiningDateUSFormatted = employee.approval_date
          ? `${employee.approval_date.split("-")[1]}/${employee.approval_date.split("-")[0]
          }/${employee.approval_date.split("-")[2]}`
          : "";

        return (
          (employee.full_name &&
            employee.full_name.toLowerCase().includes(searchLower)) ||
          (employee.mobile_number &&
            employee.mobile_number.includes(searchTerm)) ||
          (employee.employee_id &&
            employee.employee_id.toLowerCase().includes(searchLower)) ||
          (employee.email &&
            employee.email.toLowerCase().includes(searchLower)) ||
          (salaryAmount && salaryAmount.toString().includes(searchTerm)) ||
          (overtimeSalaryAmount &&
            overtimeSalaryAmount.toString().includes(searchTerm)) ||
          (employee.locationName &&
            employee.locationName.toLowerCase().includes(searchLower)) ||
          (employee.approval_date &&
            employee.approval_date.includes(searchTerm)) ||
          (joiningDateFormatted && joiningDateFormatted.includes(searchTerm)) ||
          (joiningDateUSFormatted &&
            joiningDateUSFormatted.includes(searchTerm))
        );
      });
    }

    if (startDateFilter || endDateFilter) {
      filtered = filtered.filter((employee) => {
        if (!employee.approval_date || employee.approval_date === "null")
          return false;

        try {
          const [day, month, year] = employee.approval_date.split("-");
          const employeeDate = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );

          let includeEmployee = true;

          if (startDateFilter) {
            const startDate = new Date(startDateFilter);
            startDate.setHours(0, 0, 0, 0);
            includeEmployee = includeEmployee && employeeDate >= startDate;
          }

          if (endDateFilter) {
            const endDate = new Date(endDateFilter);
            endDate.setHours(23, 59, 59, 999);
            includeEmployee = includeEmployee && employeeDate <= endDate;
          }

          return includeEmployee;
        } catch (error) {
          console.error("Error parsing date:", employee.approval_date, error);
          return false;
        }
      });
    }
    if (filterStatus === "assigned") {
      filtered = filtered.filter((emp) => emp.payDate || emp.periodType);
    } else if (filterStatus === "unassigned") {
      filtered = filtered.filter((emp) => !emp.payDate && !emp.periodType);
    }

    return filtered;
  }, [employees, searchTerm, filterStatus, startDateFilter, endDateFilter]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Parse salary data from JSON string
  const getSalaryAmount = (salaryJson) => {
    try {
      if (!salaryJson) return "0";
      const salaryData = JSON.parse(salaryJson);
      return salaryData.length > 0
        ? `₹${salaryData[0].amount}/${salaryData[0].salaryType}`
        : "0";
    } catch (error) {
      return "0";
    }
  };

  // Parse overtime salary data from JSON string
  const getOvertimeSalaryAmount = (overtimeSalaryJson) => {
    try {
      if (!overtimeSalaryJson) return "0";
      const overtimeSalaryData = JSON.parse(overtimeSalaryJson);
      return overtimeSalaryData.length > 0
        ? `₹${overtimeSalaryData[0].amount}/${overtimeSalaryData[0].salaryType}`
        : "0";
    } catch (error) {
      return "0";
    }
  };

  // Get joining date from approval_date field
  const getJoiningDate = (approvalDate) => {
    try {
      if (!approvalDate) return "-";
      // Parse date in DD-MM-YYYY format
      const [day, month, year] = approvalDate.split("-");
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  // Format pay date for display
  const formatPayDate = (dateString) => {
    try {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return null;
    }
  };

  // Calculate experience in days from approval_date
  const calculateExperienceInDays = (approvalDate) => {
    try {
      if (!approvalDate) return "0";

      // Parse date in DD-MM-YYYY format
      const [day, month, year] = approvalDate.split("-");
      const joiningDate = new Date(year, month - 1, day);
      const currentDate = new Date();

      // Calculate the difference in time
      const timeDifference = currentDate.getTime() - joiningDate.getTime();

      // Calculate the difference in days
      const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));

      return daysDifference >= 0 ? daysDifference : 0;
    } catch (error) {
      return "0";
    }
  };

  // Get experience badge class based on experience days
  const getExperienceBadgeClass = (experienceDays) => {
    if (experienceDays === "N/A") return "badge bg-secondary";

    const days = parseInt(experienceDays);
    if (days < 30) return "badge bg-light text-dark"; // Less than 1 month
    if (days < 365) return "badge bg-light text-dark"; // Less than 1 year
    if (days < 1095) return "badge bg-light text-dark"; // Less than 3 years
    return "badge bg-success"; // 3+ years
  };

  // Format experience display
  const formatExperienceDisplay = (experienceDays) => {
    if (experienceDays === "N/A") return "N/A";

    const days = parseInt(experienceDays);
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const remainingDays = days % 30;

    if (years > 0) {
      return `${years}Y ${months}M ${remainingDays}D`;
    } else if (months > 0) {
      return `${months}M ${remainingDays}D`;
    } else {
      return `${days}D`;
    }
  };

  // Handle assign date button click
  const handleAssignDateClick = (employee) => {
    setSelectedEmployee(employee);
    setPayDate("");
    setPeriodType("");
    setIsEditing(false);
    setShowPopup(true);
  };

  // Handle edit pay date button click
  const handleEditPayDateClick = (employee) => {
    setSelectedEmployee(employee);
    setPayDate(employee.payDate || "");
    setPeriodType(employee.periodType || "");
    setIsEditing(true);
    setShowPopup(true);
  };

  // Handle popup close
  const handlePopupClose = () => {
    setShowPopup(false);
    setSelectedEmployee(null);
    setPayDate("");
    setPeriodType("");
    setIsSubmitting(false);
    setIsEditing(false);
  };

  // Handle pay date change
  const handlePayDateChange = (e) => {
    setPayDate(e.target.value);
    if (e.target.value) {
      setPeriodType(""); // Clear period type when pay date is set
    }
  };

  // Handle period type change
  const handlePeriodTypeChange = (e) => {
    setPeriodType(e.target.value);
    if (e.target.value) {
      setPayDate(""); // Clear pay date when period type is set
    }
  };

  // UPDATED FUNCTION: Handle pay date submission with history logging
  const handleSubmitPayDate = async () => {
    if (!payDate && !periodType) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "Select any one",
        message: "Please enter either a pay date or select a period type",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Store old values for history logging
      const oldPayDate = selectedEmployee.payDate || "N/A";
      const oldPeriodType = selectedEmployee.periodType || "N/A";

      // Prepare the data to send
      const updateData = {
        ...selectedEmployee,
        payDate: payDate,
        periodType: periodType,
      };

      // Get the employee ID for the endpoint
      const employeeId =
        selectedEmployee.id ||
        selectedEmployee.employee_id ||
        selectedEmployee.mobile_number;

      if (!employeeId) {
        throw new Error("Employee ID not found");
      }

      // Construct the API endpoint with the employee ID
      const apiEndpoint = `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${employeeId}`;

      console.log("API Endpoint:", apiEndpoint);
      console.log("Sending data:", updateData);

      const response = await fetch(apiEndpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          `API Error: ${response.status} - ${responseData.message || "Unknown error"
          }`
        );
      }

      doAuditLog("Update", "Assign PayPeriod", selectedEmployee, updateData);
      // Update the local state
      setEmployees((prevEmployees) =>
        prevEmployees.map((emp) =>
          (emp.id || emp.employee_id || emp.mobile_number) === employeeId
            ? { ...emp, payDate: payDate, periodType: periodType }
            : emp
        )
      );

      // Success alert
      const actionText = isEditing ? "updated" : "assigned";
      const updateMessage = payDate
        ? `Pay date ${payDate} ${actionText} for ${selectedEmployee.full_name} successfully!`
        : `Period type ${periodType} ${actionText} for ${selectedEmployee.full_name} successfully!`;

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: updateMessage,
        duration: 2500, // auto-close in 2.5s
      });

      // Close popup
      handlePopupClose();
    } catch (error) {
      console.error("Error updating pay date/period type:", error);

      // Error alerts
      if (error.message.includes("API Error")) {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Failed",
          message: `Failed to ${isEditing ? "update" : "assign"
            } pay date/period type: ${error.message}`,
          duration: 2500, // auto-close in 2.5s
        });
      } else if (error.message.includes("Failed to fetch")) {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Failed",
          message:
            "Network error: Please check your internet connection and try again.",
          duration: 2500, // auto-close in 2.5s
        });
      } else {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Failed",
          message: `Failed to ${isEditing ? "update" : "assign"
            } pay date/period type: ${error.message}. Please try again.`,
          duration: 2500, // auto-close in 2.5s
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee List", 14, 16);
    const tableColumn = [
      "Full Name",
      "Mail ID",
      "Mobile Number",
      "Employee ID",
      "Joining Date",
      "Salary",
      "Overtime Salary",
      "Experience",
    ];
    const tableRows = currentEmployees.map((employee) => [
      employee.full_name || "N/A",
      employee.email || "N/A",
      employee.mobile_number || "N/A",
      employee.employee_id || "N/A",
      getJoiningDate(employee.approval_date),
      getSalaryAmount(employee.salary),
      getOvertimeSalaryAmount(employee.overTimeSalary),
      formatExperienceDisplay(
        calculateExperienceInDays(employee.approval_date)
      ),
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Employee_List.pdf");
  };

  const handleDownloadExcel = () => {
    const excelData = currentEmployees.map((employee) => ({
      "Full Name": employee.full_name || "N/A",
      "Mail ID": employee.email || "N/A",
      "Mobile Number": employee.mobile_number || "N/A",
      "Employee ID": employee.employee_id || "N/A",
      "Joining Date": getJoiningDate(employee.approval_date),
      Salary: getSalaryAmount(employee.salary),
      "Overtime Salary": getOvertimeSalaryAmount(employee.overTimeSalary),
      Experience: formatExperienceDisplay(
        calculateExperienceInDays(employee.approval_date)
      ),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee List");
    XLSX.writeFile(workbook, "Employee_List.xlsx");
  };

  const PaginationControls = ({ position = "bottom" }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          Showing {startIndex + 1} to{" "}
          {Math.min(endIndex, filteredEmployees.length)} of{" "}
          {filteredEmployees.length} entries
        </div>

        <nav aria-label="Employee pagination">
          <ul className="pagination pagination-sm mb-0">
            {/* First Page */}
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                «
              </button>
            </li>

            {/* Previous Page */}
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                ‹
              </button>
            </li>

            {/* Page Numbers */}
            {getPageNumbers().map((page, index) => (
              <li
                key={index}
                className={`page-item ${page === currentPage ? "active" : ""} ${page === "..." ? "disabled" : ""
                  }`}
              >
                {page === "..." ? (
                  <span className="page-link">...</span>
                ) : (
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}

            {/* Next Page */}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                ›
              </button>
            </li>

            {/* Last Page */}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
              >
                »
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <div className="container-fluid px-4 py-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <Loader className="spinner-border text-primary" size={40} />
            <p className="mt-3 text-muted">Loading employee data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid px-4 py-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>Failed to load employee data: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      {/* Add custom styles */}
      <style>{tableStyles}</style>

      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center justify-content-center justify-content-md-start gap-2">
            Assign Period
          </h5>
        </div>
        <div className="col-12 col-md-6 d-flex gap-2 justify-content-center justify-content-md-end text-md-end">
          <Button
            variant="outline-success"
            size="sm"
            title="Download Excel"
            onClick={handleDownloadExcel}
          >
            Export Excel
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            title="Download PDF"
            onClick={handleDownloadPDF}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {/* Search Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-4">
          <div className="mb-0">
            <label className="form-label small text-muted mb-1 d-block">
              Search
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-light border-end-0">
                <Search size={16} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-3 mt-2 mt-md-0">
          <div className="mb-0">
            <label className="form-label small text-muted mb-1">
              Assignment Status
            </label>
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>
        </div>

        <div className="col-12 col-md-5 mt-2 mt-md-0">
          <Row className="g-2 align-items-end">
            <div className="col-md-5 col-6">
              <div className="mb-0">
                <label className="form-label small text-muted mb-1">
                  Joining From
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="col-md-5 col-6">
              <div className="mb-0">
                <label className="form-label small text-muted mb-1">
                  Joining To
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  min={startDateFilter}
                />
              </div>
            </div>

            {(startDateFilter ||
              endDateFilter ||
              filterStatus !== "all" ||
              searchTerm) && (
                <div className="col-md-2 col-12">
                  <button
                    className="btn btn-outline-danger btn-sm w-100"
                    onClick={() => {
                      setStartDateFilter("");
                      setEndDateFilter("");
                      setSearchTerm("");
                      setFilterStatus("all");
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
          </Row>
        </div>
      </div>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <select
                className="form-select form-select-sm"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{ width: "80px" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show pagination with custom logic */
                (() => {
                  const pageNumbers = getPageNumbers();
                  return pageNumbers.map((page, index) => (
                    <Pagination.Item
                      key={index}
                      active={page === currentPage}
                      onClick={() => page !== "..." && handlePageChange(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </Pagination.Item>
                  ));
                })()
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {/* Employee Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              {currentEmployees.length === 0 ? (
                <div className="text-center">
                  <Users size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No employees found</h5>
                  <p className="text-muted">
                    Try adjusting your search criteria
                  </p>
                </div>
              ) : isMobile ? (
                <div>
                  {currentEmployees.map((employee, index) => (
                    <div key={employee.id || index} className="mb-3">
                      <div
                        className="card shadow-sm"
                        style={{ textAlign: "left", borderRadius: "12px" }}
                      >
                        <div className="card-body table-responsive">
                          <table className="table table-striped table-bordered table-hover mb-2">
                            <tbody>
                              <tr>
                                <td className="fw-bold">SI</td>
                                <td>{filteredEmployees.length - index}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Full Name</td>
                                <td>{employee.full_name || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Mail ID</td>
                                <td>{employee.email || "N/A"}</td>
                              </tr>
                              <tr>
                                <td
                                  style={{ minWidth: 150 }}
                                  className="fw-bold"
                                >
                                  Mobile Number
                                </td>
                                <td>{employee.mobile_number || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Employee ID</td>
                                <td>{employee.employee_id || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Joining Date</td>
                                <td>
                                  {getJoiningDate(employee.approval_date)}
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Salary</td>
                                <td>{getSalaryAmount(employee.salary)}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Overtime Salary</td>
                                <td>
                                  {getOvertimeSalaryAmount(
                                    employee.overTimeSalary
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Experience</td>
                                <td>
                                  <span
                                  // className={getExperienceBadgeClass(
                                  //   calculateExperienceInDays(
                                  //     employee.approval_date
                                  //   )
                                  // )}
                                  >
                                    {/* {formatExperienceDisplay( */}
                                    {calculateExperienceInDays(
                                      employee.approval_date
                                    )}
                                    {/* )} */}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <div className="mt-2 d-flex align-itmes-center justify-content-end">
                            {/* Actions */}
                            {(() => {
                              const hasPayDate =
                                employee.payDate && employee.payDate !== "";
                              const hasPeriodType =
                                employee.periodType &&
                                employee.periodType !== "";
                              const formattedPayDate = formatPayDate(
                                employee.payDate
                              );
                              if (hasPayDate || hasPeriodType) {
                                return (
                                  <div className="d-flex flex-column gap-1">
                                    <div className="d-flex align-items-center flex-wrap gap-1 justify-content-start">
                                      {hasPayDate && (
                                        <span className="badge bg-success text-white px-2 py-1 ">
                                          <Calendar
                                            size={12}
                                            className="me-1"
                                          />
                                          {formattedPayDate}
                                        </span>
                                      )}
                                      {hasPeriodType && (
                                        <span className="badge bg-info text-white px-2 py-1">
                                          <Clock size={12} className="me-1" />
                                          {employee.periodType} Days
                                        </span>
                                      )}
                                    </div>
                                    {canEdit && (
                                      <button
                                        className="badge bg-white text-dark px-2 py-1 border"
                                        onClick={() =>
                                          handleEditPayDateClick(employee)
                                        }
                                        title="Edit Pay Date/Period Type"
                                      >
                                        Edit
                                      </button>
                                    )}
                                  </div>
                                );
                              } else {
                                return (
                                  <>
                                    {canAdd && (
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() =>
                                          handleAssignDateClick(employee)
                                        }
                                        title="Assign Pay Date/Period Type"
                                      >
                                        <Calendar size={14} className="me-1" />
                                        Assign
                                      </button>
                                    )}
                                  </>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-bordered">
                    <thead className="">
                      <tr>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          S.I
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          Full Name
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          Mail ID
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          Mobile Number
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          Employee ID
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          {/* <Calendar size={16} className="me-1" /> */}
                          Joining Date
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          {/* <DollarSign size={16} className="me-1" /> */}
                          Salary
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          {/* <Clock size={16} className="me-1" /> */}
                          OT Salary
                        </th>
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          {/* <Clock size={16} className="me-1" /> */}
                          Pay Period
                        </th>
                        {/* <th id="text-white-important" className="fw-semibold text-dark">
                          Experience
                        </th> */}
                        <th
                          id="text-white-important"
                          className="fw-semibold text-dark"
                        >
                          {/* <Settings size={16} className="me-1" /> */}
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentEmployees.map((employee, index) => {
                        const experienceDays = calculateExperienceInDays(
                          employee.approval_date
                        );
                        const hasPayDate =
                          employee.payDate && employee.payDate !== "";
                        const hasPeriodType =
                          employee.periodType && employee.periodType !== "";
                        const formattedPayDate = formatPayDate(
                          employee.payDate
                        );

                        return (
                          <tr key={employee.id || index}>
                            <td className="text-muted">
                              {filteredEmployees.length - index}
                            </td>
                            <td className="fw-semibold text-dark">
                              {employee.full_name || "-"}
                              {/* <br /> */}
                              {/* <small className="text-muted">{employee.email || 'N/A'}</small> */}
                            </td>
                            <td className="text-dark">
                              {employee.email || "-"}
                            </td>
                            <td className="text-dark">
                              {employee.mobile_number || "-"}
                            </td>
                            <td>
                              {/* <span className="badge bg-light text-dark px-3 py-2"> */}
                              {employee.employee_id || "-"}
                              {/* </span> */}
                            </td>
                            <td className="text-dark">
                              {getJoiningDate(employee.approval_date)}
                            </td>
                            <td className="text-dark fw-semibold">
                              {getSalaryAmount(employee.salary)}
                            </td>
                            <td className="text-dark fw-semibold">
                              {getOvertimeSalaryAmount(employee.overTimeSalary)}
                            </td>
                            <td className="text-dark fw-semibold">
                              {hasPayDate || hasPeriodType ? (
                                <div className="d-flex flex-column align-items-center gap-2">
                                  {/* Badges Section */}
                                  <div className="d-flex flex-wrap justify-content-center gap-1">
                                    {hasPayDate && (
                                      // <span className="badge bg-success text-white px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                      <span>
                                        {/* <Calendar size={12} className="me-1" /> */}
                                        {formattedPayDate}
                                      </span>
                                    )}
                                    {hasPeriodType && (
                                      // <span className="badge bg-info text-white px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                      <span>
                                        {/* <Clock size={12} className="me-1" /> */}
                                        {employee.periodType} Days
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                            {/* <td>
                              <span
                              >
                                {formatExperienceDisplay(experienceDays)}
                              </span>
                            </td> */}
                            <td>
                              <div className="text-center">
                                {hasPayDate || hasPeriodType ? (
                                  <div className="d-flex flex-column align-items-center gap-2">
                                  
                                    {/* Edit Button Section */}
                                    <div className="d-flex justify-content-center">
                                      {canEdit && (
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() =>
                                            handleEditPayDateClick(employee)
                                          }
                                          title="Edit Pay Date/Period Type"
                                          className="px-3"
                                        >
                                          Edit
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  /* Assign Button Section */
                                  <div className="d-flex justify-content-center">
                                    {canAdd && (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() =>
                                          handleAssignDateClick(employee)
                                        }
                                        title="Assign Pay Date/Period Type"
                                        className="px-3"
                                      >
                                        Assign
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="text-muted d-flex align-items-center justify-content-end p-2">
        Showing {startIndex + 1} to{" "}
        {Math.min(endIndex, filteredEmployees.length)} of{" "}
        {filteredEmployees.length} entries
      </div>
      {/* Pay Date/Period Type Assignment/Edit Popup */}
      {showPopup && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  <Calendar size={20} className="me-2" />
                  {isEditing
                    ? "Edit Pay Date/Period Type"
                    : "Assign Pay Date/Period Type"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handlePopupClose}
                  disabled={isSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-3 p-3 bg-light rounded">
                    <div className="flex-grow-1">
                      <div className="d-flex gap-3">
                        <h6 className="mb-1 fw-semibold text-dark">
                          {selectedEmployee?.full_name || "N/A"}
                        </h6>
                        <small className="text-muted">
                          <strong>ID:</strong>{" "}
                          {selectedEmployee?.employee_id || "N/A"}
                        </small>
                        <small className="text-muted">
                          <strong>Mobile:</strong>{" "}
                          {selectedEmployee?.mobile_number || "N/A"}
                        </small>
                      </div>
                      {isEditing && (
                        <div className="mt-2">
                          {selectedEmployee?.payDate && (
                            <small className="text-info d-block">
                              <strong>Current Pay Date:</strong>{" "}
                              {formatPayDate(selectedEmployee.payDate)}
                            </small>
                          )}
                          {selectedEmployee?.periodType && (
                            <small className="text-info d-block">
                              <strong>Current Period Type:</strong>{" "}
                              {selectedEmployee.periodType}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3 text-start">
                    <label
                      htmlFor="payDate"
                      className="form-label fw-semibold text-dark"
                    >
                      Pay Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="payDate"
                      value={payDate}
                      onChange={handlePayDateChange}
                      disabled={isSubmitting}
                    />
                    <small className="text-muted">
                      Select a specific date when payment should be processed.
                    </small>
                  </div>

                  <div className="text-center mb-3">
                    <span className="badge bg-secondary px-3 py-2">OR</span>
                  </div>

                  <div className="mb-3 text-start">
                    <label
                      htmlFor="periodType"
                      className="form-label fw-semibold text-dark"
                    >
                      Period Type (in days)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="periodType"
                      value={periodType}
                      onChange={handlePeriodTypeChange}
                      disabled={isSubmitting}
                      placeholder="Enter PerioType"
                      min="1"
                    />
                    <small className="text-muted">
                      Enter the number of days for recurring payment scheduling.
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePopupClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitPayDate}
                  disabled={isSubmitting || (!payDate && !periodType)}
                >
                  {isSubmitting ? (
                    <>
                      <Loader
                        size={16}
                        className="me-2 spinner-border spinner-border-sm"
                      />
                      {isEditing ? "Updating..." : "Assigning..."}
                    </>
                  ) : (
                    <>
                      <Calendar size={16} className="me-2" />
                      {isEditing ? "Update" : "Assign"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
